import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';

type GenerateDataExportOptions = {
  config_id: string;
  include_sensitive_fields?: boolean;
  export_format?: 'DOCX' | 'EXCEL';
};

type GenerateDataExportResponse = {
  id: string;
  fileOwnerClientId: string;
};

type GenerateDataExportVariables = {
  resource: string;
  values: GenerateDataExportOptions;
};

export function useGenerateDataExport() {
  const queryClient = useQueryClient();

  const {
    mutateAsync,
    error,
    isPending: isLoading,
    reset,
  } = useMutation<{ data: GenerateDataExportResponse }, Error, GenerateDataExportVariables>({
    mutationFn: ({ resource, values }) => api.post<GenerateDataExportResponse>(resource, values),
    onSuccess: ({ data }) => {
      queryClient.invalidateQueries({
        queryKey: ['clients', data.fileOwnerClientId, 'data-exports'],
      });
    },
  });

  const generateClientDataExport = async (clientId: string, values: GenerateDataExportOptions) => {
    return mutateAsync({
      resource: `clients/${clientId}/data-exports`,
      values: {
        config_id: values.config_id,
        include_sensitive_fields: values.include_sensitive_fields,
        export_format: values.export_format,
      },
    });
  };

  const generateFamilyDataExport = async (familyId: string, values: GenerateDataExportOptions) => {
    return mutateAsync({
      resource: `families/${familyId}/data-exports`,
      values: {
        config_id: values.config_id,
        include_sensitive_fields: values.include_sensitive_fields,
        export_format: values.export_format,
      },
    });
  };

  const generateDataExport = async (
    type: 'client' | 'family',
    id: string,
    values: GenerateDataExportOptions,
  ) => {
    if (type === 'client') {
      return generateClientDataExport(id, values);
    } else {
      return generateFamilyDataExport(id, values);
    }
  };

  return {
    generateDataExport,
    generateClientDataExport,
    generateFamilyDataExport,
    error,
    isLoading,
    reset,
  };
}
